<script setup lang="ts">
import type { SpotlightContent, SpotlightLink } from '~kui/components/KUI/Unified/Spotlight/KUIUnifiedSpotlight.vue';
import type { BadgeDetail } from '~kui/types/badge';

import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import KUICoreInputRichTextEditorRenderer from '~kui/components/KUI/Core/Input/RichTextEditor/Renderer/KUICoreInputRichTextEditorRenderer.vue';
import { RTE_RENDERER_VARIANT } from '~kui/components/KUI/Core/Input/RichTextEditor/Renderer/types';
import KUIUnifiedBadgeProgress from '~kui/components/KUI/Unified/Badge/KUIUnifiedBadgeProgress.vue';
import KUIUnifiedBadgeProgressRow from '~kui/components/KUI/Unified/Badge/KUIUnifiedBadgeProgressRow.vue';
import KUIUnifiedSpotlight from '~kui/components/KUI/Unified/Spotlight/KUIUnifiedSpotlight.vue';
import { DEFAULT_ANYWAY_PARTNER_BG_COLOR } from '~kui/constants/anyway';

import KUIUnifiedEarnableCard from './KUIUnifiedEarnableCard.vue';

interface Props {
  badge: BadgeDetail;
  animationsEnabled?: boolean;
  spotlight?: SpotlightContent | null;
}

const props = withDefaults(defineProps<Props>(), {
  animationsEnabled: true,
  spotlight: null,
});

defineEmits<{
  start: [];
  spotlightLinkClick: [link: SpotlightLink];
}>();

const hasBackgroundImage = computed(() => !!props.badge.coverImage?.url);

const staticBadgeUrl = computed(() => props.badge.iconFile?.url ?? null);

// KUIUnifiedBadgeProgress owns the model and its load-failure fallback, but this component
// lays the hero out differently for the 3D badge (progress moves under the title), so it
// tracks the failure too. Otherwise the layout keeps describing a model that isn't there.
const premiumBadgeFailed = ref(false);
const premiumBadgeUrl = computed(() => {
  if (premiumBadgeFailed.value || !props.animationsEnabled) {
    return null;
  }
  // Unearned badges keep the grayscale static badge, which a full-colour model can't
  // convey. Only when there IS one: an icon-less badge would render nothing at all.
  if (!props.badge.earned && staticBadgeUrl.value) {
    return null;
  }
  return props.badge.premiumBadgeFile?.url ?? null;
});

// shrink-0 on the hero's wrapper keeps it at its designed height inside the
// h-screen flex column — without it, overflowing content compresses the hero,
// so the page visibly jumps when the loading skeleton (full height) swaps to
// the real banner (compressed).
const heroVariants = cva('relative flex h-96 w-full select-none items-center justify-center rounded-2xl sm:rounded-3xl bg-cover bg-center p-3 py-5 shadow-lg', {
  variants: {
    hasBackground: {
      true: '',
      false: 'bg-linear-to-b from-secondary-subtle to-secondary',
    },
  },
});

const heroClasses = computed(() => twMerge(heroVariants({ hasBackground: hasBackgroundImage.value })));
</script>

<template>
  <div class="relative flex h-full flex-col">
    <!-- Hero — matches the content column width below -->
    <div class="mx-auto w-full shrink-0 px-4 pt-4 sm:max-w-4xl lg:max-w-5xl">
      <div
        :class="heroClasses"
        :style="{ backgroundImage: hasBackgroundImage ? `url(${badge.coverImage?.url})` : undefined }"
      >
        <KUIUnifiedBadgeProgress
          v-if="staticBadgeUrl || premiumBadgeUrl"
          :image-url="staticBadgeUrl"
          :image-alt="badge.name"
          badge-size="size-58"
          :progress="badge.progress"
          :premium-badge-url="premiumBadgeUrl"
          :hide-progress-row="!!premiumBadgeUrl"
          :icon-path="badge.partnerIconPath ?? '/images/kui/unified/anyway-shield-badge.webp'"
          :partner-bg-color="badge.partnerBgColor ?? DEFAULT_ANYWAY_PARTNER_BG_COLOR"
          @premium-error="premiumBadgeFailed = true"
        />
      </div>
    </div>

    <!-- Content -->
    <KUIUnifiedLayoutContainer>
      <div class="my-8 flex flex-col gap-8 rounded-2xl border border-border p-5">
        <div class="flex flex-col">
          <div class="mb-2 flex w-full justify-between">
            <Heading
              as="h1"
              size="2xl"
              weight="semibold"
            >
              {{ badge.name }}
            </Heading>
            <div v-if="badge.partnerIconPath" class="flex aspect-square size-14 items-center justify-center rounded-full bg-background">
              <NuxtImg :src="badge.partnerIconPath" :alt="badge.partner?.name ?? ''" />
            </div>
          </div>
          <div class="flex items-baseline gap-4">
            <Text v-if="badge.partner?.name" size="md">
              {{ $t('unified.wallet.detail.by', { name: badge.partner.name }) }}
            </Text>
            <Text
              v-if="badge.publishedAt"
              size="sm"
              class="text-muted-foreground"
            >
              {{ badge.publishedAt }}
            </Text>
          </div>

          <!-- The static badge shows progress inside the hero (KUIUnifiedBadgeProgress);
               the premium 3D badge keeps its hero clear, so progress sits under the title -->
          <div v-if="premiumBadgeUrl && badge.progress?.length" class="mt-3 flex">
            <KUIUnifiedBadgeProgressRow
              :progress="badge.progress ?? []"
              :icon-path="badge.partnerIconPath ?? '/images/kui/unified/anyway-shield-badge.webp'"
              :partner-bg-color="badge.partnerBgColor ?? DEFAULT_ANYWAY_PARTNER_BG_COLOR"
            />
          </div>
        </div>

        <div class="grid grid-flow-col auto-cols-fr items-center gap-4">
          <KUIUnifiedEarnableCard
            v-if="badge.coinsAvailable"
            :value="badge.coinsAvailable"
            :label="$t('unified.wallet.detail.coinsAvailable')"
            icon-path="/images/kui/unified/gold-coin.webp"
            variant="lime"
          />
          <KUIUnifiedEarnableCard
            :value="badge.skillsCount"
            :label="$t('unified.wallet.detail.associatedSkills')"
            variant="blue"
          />
          <KUIUnifiedEarnableCard
            v-if="badge.prize"
            value="1"
            :label="$t('unified.wallet.detail.prize')"
            variant="pink"
          />
        </div>

        <KUIUnifiedSpotlight
          v-if="spotlight"
          :heading="spotlight.heading"
          :image-url="spotlight.imageUrl"
          :image-alt="spotlight.imageAlt"
          :links="spotlight.links"
          @link-click="$emit('spotlightLinkClick', $event)"
        />

        <KUICoreInputRichTextEditorRenderer
          v-if="badge.description"
          :content="badge.description"
          :variant="RTE_RENDERER_VARIANT.PARAGRAPH"
        />
      </div>

      <!-- Spacer -->
      <div class="flex-1" />

      <Text
        v-if="badge.disclaimer"
        size="xs"
        class="text-muted-foreground whitespace-pre-line pb-6 text-center italic"
        data-testid="badge-disclaimer"
      >
        {{ badge.disclaimer }}
      </Text>

      <!-- CTA -->
      <div v-if="badge.collectionSlug && !badge.isAllCompleted" class="sticky bottom-0 flex w-full items-center justify-center bg-background py-2">
        <Button
          variant="outline"
          size="default"
          class="w-full"
          type="button"
          @click="$emit('start')"
        >
          {{ badge.hasInProgress ? $t('unified.wallet.detail.continue') : $t('unified.wallet.detail.getBadge') }}
        </Button>
      </div>
    </KUIUnifiedLayoutContainer>
  </div>
</template>
